import { useEffect, useState, useCallback } from 'react';
import { Save, Eye, EyeOff, AlertTriangle, CheckCircle, Wifi, Bell } from 'lucide-react';
import { api } from '../../api/client';

type Tenant = { id: string; name: string };

type Settings = {
  apiKey: string;
  hasApiKey?: boolean;
  environment: 'demo' | 'production';
  webhookUrl?: string;
  webhookValidUntil?: string;
  isSupplier: boolean;
  isCustomer: boolean;
  isCarrier: boolean;
  companyName: string;
  taxId: string;
  jbkjs: string;
  registrationNumber: string;
  address: string;
  city: string;
  postalCode: string;
  contactEmail: string;
};

const emptySettings: Settings = {
  apiKey: '',
  hasApiKey: false,
  environment: 'demo',
  isSupplier: true,
  isCustomer: true,
  isCarrier: false,
  companyName: '',
  taxId: '',
  jbkjs: '',
  registrationNumber: '',
  address: '',
  city: '',
  postalCode: '',
  contactEmail: '',
};

export function EOtpremnicaSettingsPanel() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [form, setForm] = useState<Settings>(emptySettings);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    api.get<Tenant[]>('/admin/tenants').then((rows) => {
      setTenants(rows);
      if (rows.length > 0) setSelectedTenantId(rows[0].id);
    }).catch(() => {});
  }, []);

  const fetchSettings = useCallback(async () => {
    if (!selectedTenantId) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const data = await api.get<Settings>(`/admin/eotpremnica/settings/${selectedTenantId}`);
      setForm({ ...emptySettings, ...data });
    } catch (e: any) {
      if (e?.status !== 404) setError(e?.data?.message || e?.message || 'Greška pri učitavanju');
    } finally {
      setLoading(false);
    }
  }, [selectedTenantId]);

  useEffect(() => { if (selectedTenantId) fetchSettings(); }, [selectedTenantId, fetchSettings]);

  const set = <K extends keyof Settings>(key: K, value: Settings[K]) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTenantId) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await api.put(`/admin/eotpremnica/settings/${selectedTenantId}`, form);
      setSuccess('Podešavanja sačuvana.');
      await fetchSettings();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška pri čuvanju');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!selectedTenantId) return;
    setTesting(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await api.post<{ companyCount: number }>(`/admin/eotpremnica/settings/${selectedTenantId}/test-connection`, {});
      setSuccess(`Konekcija uspešna. Portal vraća ${res.companyCount} firmi.`);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Konekcija neuspela');
    } finally {
      setTesting(false);
    }
  };

  const handleSubscribeWebhook = async () => {
    if (!selectedTenantId) return;
    setSubscribing(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await api.post<{ validToUtc: string }>(`/admin/eotpremnica/settings/${selectedTenantId}/subscribe-webhook`, {});
      setSuccess(`Webhook pretplata aktivna do ${new Date(res.validToUtc).toLocaleString('sr-Latn-RS')}.`);
      await fetchSettings();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Pretplata neuspela');
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <label style={{ fontSize: 12, color: 'var(--color-text-muted)', fontWeight: 500 }}>Tenant:</label>
        <select value={selectedTenantId} onChange={(e) => setSelectedTenantId(e.target.value)} style={selectStyle}>
          {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>

      {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
      {success && <div style={successBox}><CheckCircle size={14} /> {success}</div>}

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-text-muted)' }}>Učitavanje...</div>
      ) : (
        <form onSubmit={handleSave}>
          <Section title="API konfiguracija">
            <Field label={`API Key${form.hasApiKey ? ' (konfigurisan)' : ''}`}>
              <div style={{ position: 'relative' }}>
                <input
                  type={showApiKey ? 'text' : 'password'}
                  value={form.apiKey}
                  onChange={(e) => set('apiKey', e.target.value)}
                  style={inputStyle}
                  placeholder={form.hasApiKey ? '•••••••••• (ostavi prazno da ne menjaš)' : 'eOtpremnica API ključ'}
                />
                <button type="button" onClick={() => setShowApiKey(!showApiKey)} style={toggleBtn}>
                  {showApiKey ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </Field>
            <Field label="Okruženje">
              <select value={form.environment} onChange={(e) => set('environment', e.target.value as any)} style={selectStyle}>
                <option value="demo">Demo</option>
                <option value="production">Production</option>
              </select>
            </Field>
          </Section>

          <Section title="Uloge tenanta u sistemu">
            <div style={{ display: 'flex', gap: 20 }}>
              <label style={checkboxLabel}>
                <input type="checkbox" checked={form.isSupplier} onChange={(e) => set('isSupplier', e.target.checked)} />
                Pošiljalac
              </label>
              <label style={checkboxLabel}>
                <input type="checkbox" checked={form.isCustomer} onChange={(e) => set('isCustomer', e.target.checked)} />
                Primalac
              </label>
              <label style={checkboxLabel}>
                <input type="checkbox" checked={form.isCarrier} onChange={(e) => set('isCarrier', e.target.checked)} />
                Prevoznik
              </label>
            </div>
          </Section>

          <Section title="Podaci firme (za UBL)">
            <Field label="Naziv firme">
              <input type="text" value={form.companyName} onChange={(e) => set('companyName', e.target.value)} style={inputStyle} />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
              <Field label="PIB"><input type="text" value={form.taxId} onChange={(e) => set('taxId', e.target.value)} style={inputStyle} /></Field>
              <Field label="Matični broj"><input type="text" value={form.registrationNumber} onChange={(e) => set('registrationNumber', e.target.value)} style={inputStyle} /></Field>
              <Field label="JBKJS"><input type="text" value={form.jbkjs} onChange={(e) => set('jbkjs', e.target.value)} style={inputStyle} /></Field>
            </div>
            <Field label="Adresa"><input type="text" value={form.address} onChange={(e) => set('address', e.target.value)} style={inputStyle} /></Field>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14 }}>
              <Field label="Grad"><input type="text" value={form.city} onChange={(e) => set('city', e.target.value)} style={inputStyle} /></Field>
              <Field label="Poštanski broj"><input type="text" value={form.postalCode} onChange={(e) => set('postalCode', e.target.value)} style={inputStyle} /></Field>
            </div>
            <Field label="Kontakt email"><input type="email" value={form.contactEmail} onChange={(e) => set('contactEmail', e.target.value)} style={inputStyle} /></Field>
          </Section>

          <Section title="Webhook">
            <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 8 }}>
              Pretplata traje 24h. Potreban je cron koji pokreće obnovu svakog dana pre ponoći.
            </div>
            {form.webhookUrl ? (
              <div style={{ fontSize: 12, color: 'var(--color-text)', marginBottom: 8, fontFamily: 'Menlo, Consolas, monospace', wordBreak: 'break-all' }}>
                URL: {form.webhookUrl}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: 'var(--color-text-muted)', fontStyle: 'italic', marginBottom: 8 }}>
                Webhook URL nije registrovan (trenutno koristi se PULL)
              </div>
            )}
            {form.webhookValidUntil && (
              <div style={{ fontSize: 12, color: new Date(form.webhookValidUntil) > new Date() ? '#86efac' : '#fca5a5', marginBottom: 8 }}>
                Pretplata važi do: {new Date(form.webhookValidUntil).toLocaleString('sr-Latn-RS')}
              </div>
            )}
            <button type="button" onClick={handleSubscribeWebhook} disabled={subscribing} style={btnSecondary}>
              <Bell size={14} /> {subscribing ? 'Pretplata...' : 'Pretplati webhook (24h)'}
            </button>
          </Section>

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button type="button" onClick={handleTestConnection} disabled={testing} style={btnSecondary}>
              <Wifi size={14} /> {testing ? 'Test...' : 'Testiraj konekciju'}
            </button>
            <div style={{ flex: 1 }} />
            <button type="submit" disabled={saving} style={btnPrimary}>
              <Save size={14} /> {saving ? 'Čuvanje...' : 'Sačuvaj'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 14, background: 'var(--color-bg)', borderRadius: 8, border: '1px solid var(--color-border)', marginBottom: 16 }}>
      <h4 style={{ margin: 0, fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{title}</h4>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4, fontWeight: 500 }}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, width: '100%', background: 'var(--color-bg-surface)', color: 'var(--color-text)' };
const selectStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, background: 'var(--color-bg-surface)', color: 'var(--color-text)', minWidth: 200 };
const btnPrimary: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 6, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnSecondary: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: 'var(--color-bg-surface)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500 };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 16 };
const successBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(34,197,94,0.15)', color: '#4ade80', fontSize: 13, marginBottom: 16 };
const toggleBtn: React.CSSProperties = { position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', padding: 4 };
const checkboxLabel: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--color-text)', cursor: 'pointer' };
